import React, {FC, useCallback, useMemo, useRef, useState} from "react";
import Axios from "axios";
import {
    ActionButton,
    ChoiceGroup,
    DefaultButton,
    IChoiceGroupOption,
    Stack,
    Text,
    TextField
} from "@fluentui/react";
import {SharedColors} from "@fluentui/theme";
import {AresDeploymentEnvironment} from "../API";
import {GetApiError} from "./ShowApiError";
import {LoadingButton} from "./LoadingButton";
import {OnixCallout} from "./OnixCallout";

type TCalloutToggle = (
    e: React.MouseEvent<any, any> | undefined,
    data: any,
    forceStatus?: boolean,
    renderData?: any,
) => void;

type WorkflowStepKey = "pull" | "tests" | "snapshot" | "environment";
type WorkflowStepStatus = "pending" | "running" | "success" | "failed";

type AresDeploymentGroupSnapshotSummary = {
    id: number;
};

interface IAresDeploymentPrepareSnapshotCalloutProps {
    disabled?: boolean;
    groupId?: number;
    groupName?: string;
    keyData?: string;
    onSnapshotCreated?: () => void | Promise<void>;
    onTestsFinished?: () => void | Promise<void>;
    onVersionsPulled?: () => void | Promise<void>;
    suggestedVersionName?: string;
}

const defaultStepStatuses: Record<WorkflowStepKey, WorkflowStepStatus> = {
    pull: "pending",
    tests: "pending",
    snapshot: "pending",
    environment: "pending",
};

const workflowSteps: Array<{key: WorkflowStepKey; label: string}> = [
    {key: "pull", label: "Povuci verzije"},
    {key: "tests", label: "Pokreni testove"},
    {key: "snapshot", label: "Napravi snapshot"},
    {key: "environment", label: "Postavi environment"},
];

const targetEnvironmentOptions: IChoiceGroupOption[] = [
    {key: `${AresDeploymentEnvironment.Testing}`, text: "Testing"},
    {key: `${AresDeploymentEnvironment.Staging}`, text: "Staging"},
    {key: `${AresDeploymentEnvironment.Production}`, text: "Production"},
];

const formatStatus = (status: WorkflowStepStatus) => {
    switch (status) {
        case "running":
            return "Radim...";
        case "success":
            return "Gotovo";
        case "failed":
            return "Greška";
        default:
            return "Čeka";
    }
};

const getStepLabel = (stepKey: WorkflowStepKey) => {
    return workflowSteps.find(step => step.key === stepKey)?.label ?? "";
};

const safeRun = async (callback?: () => void | Promise<void>) => {
    try {
        await callback?.();
    } catch {
    }
};

export const AresDeploymentPrepareSnapshotCallout: FC<IAresDeploymentPrepareSnapshotCalloutProps> = ({
    disabled,
    groupId,
    groupName,
    keyData,
    onSnapshotCreated,
    onTestsFinished,
    onVersionsPulled,
    suggestedVersionName,
}) => {
    const [running, setRunning] = useState(false);
    const [name, setName] = useState("");
    const [notes, setNotes] = useState("");
    const [targetEnvironment, setTargetEnvironment] = useState<AresDeploymentEnvironment>(
        AresDeploymentEnvironment.Testing
    );
    const [stepStatuses, setStepStatuses] = useState<Record<WorkflowStepKey, WorkflowStepStatus>>(defaultStepStatuses);
    const [error, setError] = useState<string>();
    const [successMessage, setSuccessMessage] = useState<string>();
    const toggleRef = useRef<TCalloutToggle>();

    const effectiveSuggestedVersionName = useMemo(() => {
        return suggestedVersionName?.trim() || "sljedeći vX";
    }, [suggestedVersionName]);

    const reset = useCallback(() => {
        setName("");
        setNotes("");
        setTargetEnvironment(AresDeploymentEnvironment.Testing);
        setStepStatuses(defaultStepStatuses);
        setError(undefined);
        setSuccessMessage(undefined);
    }, []);

    const dismiss = useCallback(() => {
        if (!running) {
            reset();
            toggleRef.current?.(undefined, undefined, false);
        }
    }, [reset, running]);

    const setStepStatus = useCallback((stepKey: WorkflowStepKey, status: WorkflowStepStatus) => {
        setStepStatuses(prev => ({
            ...prev,
            [stepKey]: status,
        }));
    }, []);

    const markFailed = useCallback((stepKey: WorkflowStepKey, errorValue: unknown) => {
        setStepStatus(stepKey, "failed");
        const message = GetApiError(errorValue) ?? "Nepoznata greška.";
        setError(`${getStepLabel(stepKey)} nije uspio: ${message}`);
    }, [setStepStatus]);

    const runWorkflow = useCallback(async () => {
        if (!groupId || running) {
            return;
        }

        setRunning(true);
        setError(undefined);
        setSuccessMessage(undefined);
        setStepStatuses(defaultStepStatuses);

        let snapshotCreated = false;

        try {
            setStepStatus("pull", "running");
            try {
                await Axios.post(`/AresDeploymentAres/PullVersionsForGroup?groupId=${groupId}`);
                setStepStatus("pull", "success");
                await safeRun(onVersionsPulled);
            } catch (e) {
                markFailed("pull", e);
                return;
            }

            setStepStatus("tests", "running");
            try {
                await Axios.post(`/AresDeploymentAres/RunTestsForGroup?groupId=${groupId}`);
                setStepStatus("tests", "success");
            } catch (e) {
                markFailed("tests", e);
                return;
            } finally {
                await safeRun(onTestsFinished);
            }

            setStepStatus("snapshot", "running");
            let snapshotId: number | undefined;
            try {
                const {data} = await Axios.post<AresDeploymentGroupSnapshotSummary>(
                    `/AresDeploymentGroupSnapshot/CreateForGroup?groupId=${groupId}`,
                    {
                        name: name.trim() || undefined,
                        notes: notes.trim() || undefined,
                    }
                );
                snapshotId = data?.id;
                snapshotCreated = true;

                if (!snapshotId) {
                    throw new Error("Snapshot je kreiran, ali odgovor ne sadrži ID snapshota.");
                }

                setStepStatus("snapshot", "success");
            } catch (e) {
                markFailed("snapshot", e);
                return;
            }

            setStepStatus("environment", "running");
            try {
                await Axios.post(
                    `/AresDeploymentGroupSnapshot/SetEnvironment?snapshotId=${snapshotId}&environment=${targetEnvironment}&enabled=true`
                );
                setStepStatus("environment", "success");
                setSuccessMessage("Gotovo.");
            } catch (e) {
                markFailed("environment", e);
            }
        } finally {
            if (snapshotCreated) {
                await safeRun(onSnapshotCreated);
            }
            setRunning(false);
        }
    }, [
        groupId,
        markFailed,
        name,
        notes,
        onSnapshotCreated,
        onTestsFinished,
        onVersionsPulled,
        running,
        setStepStatus,
        targetEnvironment,
    ]);

    return (
        <OnixCallout
            keyData={keyData}
            titleless
            heightless
            minWidth={460}
            customMaxWidth={560}
            onDismiss={() => {
                if (!running) {
                    reset();
                }
            }}
            trigger={(id, show) => {
                toggleRef.current = show;

                return (
                    <ActionButton
                        id={id}
                        disabled={disabled || running || !groupId}
                        iconProps={{iconName: "Rocket"}}
                        onClick={(event) => show(event, undefined)}
                    >
                        {running ? "Pripremam snapshot..." : "Pripremi snapshot"}
                    </ActionButton>
                );
            }}
        >
            {(realign) => (
                <Stack tokens={{childrenGap: 10, padding: 12}} style={{maxWidth: 500}}>
                    <Stack tokens={{childrenGap: 4}}>
                        <Text variant={"mediumPlus"}>
                            {groupName ? `Pripremi snapshot za "${groupName}"` : "Pripremi snapshot"}
                        </Text>
                        <Text variant={"small"}>
                            Redoslijed: Povuci verzije, Pokreni testove, Napravi snapshot.
                        </Text>
                    </Stack>

                    <ChoiceGroup
                        label={"Snapshot postavi kao"}
                        options={targetEnvironmentOptions}
                        selectedKey={`${targetEnvironment}`}
                        disabled={running}
                        onChange={(_, option) => {
                            if (!option) {
                                return;
                            }
                            setTargetEnvironment(Number(option.key) as AresDeploymentEnvironment);
                            setError(undefined);
                            setSuccessMessage(undefined);
                        }}
                    />

                    <TextField
                        label={"Naziv verzije"}
                        value={name}
                        placeholder={effectiveSuggestedVersionName}
                        disabled={running}
                        onFocus={realign}
                        onChange={(_, value) => {
                            setName(value ?? "");
                            setError(undefined);
                            setSuccessMessage(undefined);
                        }}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" && !event.shiftKey) {
                                event.preventDefault();
                                void runWorkflow();
                            }
                        }}
                    />

                    <TextField
                        label={"Opis verzije"}
                        value={notes}
                        multiline
                        rows={4}
                        disabled={running}
                        onFocus={realign}
                        onChange={(_, value) => {
                            setNotes(value ?? "");
                            setError(undefined);
                            setSuccessMessage(undefined);
                        }}
                    />

                    <Stack tokens={{childrenGap: 5}}>
                        {workflowSteps.map(step => (
                            <Stack key={step.key} horizontal horizontalAlign={"space-between"}>
                                <Text variant={"small"}>{step.label}</Text>
                                <Text variant={"small"}>{formatStatus(stepStatuses[step.key])}</Text>
                            </Stack>
                        ))}
                    </Stack>

                    {error ? (
                        <Text role={"alert"} styles={{root: {color: SharedColors.red20}}}>
                            {error}
                        </Text>
                    ) : null}

                    {successMessage ? (
                        <Text role={"status"} styles={{root: {color: SharedColors.green20}}}>
                            {successMessage}
                        </Text>
                    ) : null}

                    <Stack horizontal horizontalAlign={"end"} tokens={{childrenGap: 8}}>
                        <DefaultButton disabled={running} text={successMessage ? "Gotovo" : "Odustani"} onClick={dismiss}/>
                        <LoadingButton
                            loading={running}
                            disabled={running || !groupId}
                            text={running ? "Radim..." : "Pokreni sve"}
                            onClick={() => void runWorkflow()}
                        />
                    </Stack>
                </Stack>
            )}
        </OnixCallout>
    );
};
